import { MediaVideo } from "@/components/admin/MediaAsset";
import { useEffect, useRef, useState } from "react";
import { Loader2, Camera } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileUrl } from "@/components/admin/VideoUploader";

const fmt = (t) => `${Math.floor(t / 60)}:${String(Math.floor(t % 60)).padStart(2, "0")}.${Math.floor((t % 1) * 10)}`;

export default function FrameCoverPicker({ open, onClose, videoUrl, onPick }) {
  const ref = useRef(null);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => { if (open) { setTime(0); setDuration(0); setFailed(false); } }, [open]);

  const seek = (t) => { setTime(t); if (ref.current) ref.current.currentTime = t; };

  const capture = async () => {
    const v = ref.current;
    if (!v || !v.videoWidth) { toast.error("Video belum siap — tunggu sebentar"); return; }
    setSaving(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = v.videoWidth;
      canvas.height = v.videoHeight;
      canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      if (!blob) throw new Error("Gagal mengambil frame");
      const fd = new FormData();
      fd.append("file", blob, "frame.png");
      const r = await api.post("/upload/cover", fd);
      onPick(fileUrl(r.data.path));
      toast.success(`Cover diambil dari detik ${fmt(v.currentTime)}`);
    } catch (e) { toast.error(e?.response ? apiError(e) : `Gagal mengambil frame: ${e.message}`); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#FFFFFF] border-[#E2E8F0] text-slate-900 max-w-lg" data-testid="frame-cover-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Ambil Cover dari Frame Video</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">Geser slider ke momen terbaik, lalu klik "Gunakan Frame Ini".</DialogDescription>
        </DialogHeader>
        <MediaVideo
          ref={ref}
          src={videoUrl}
          crossOrigin="anonymous"
          preload="auto"
          muted
          playsInline
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
          onError={() => setFailed(true)}
          onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
          className="w-full max-h-[50vh] rounded-xl bg-black object-contain"
          data-testid="frame-cover-video"
        />
        {failed && <p className="text-xs text-[#E11D48]" data-testid="frame-cover-error">Video tidak bisa diputar di browser ini — pilih cover dari Galeri Media.</p>}
        <div className="flex items-center gap-3">
          <Slider value={[time]} min={0} max={duration || 1} step={0.1} onValueChange={([t]) => seek(t)} disabled={!duration} className="flex-1" data-testid="frame-cover-slider" />
          <span className="text-xs font-mono-code text-slate-600 w-24 text-right" data-testid="frame-cover-time">{fmt(time)} / {fmt(duration)}</span>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-xl" data-testid="frame-cover-cancel">Batal</Button>
          <Button onClick={capture} disabled={saving || !duration} className="bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl" data-testid="frame-cover-use">
            {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Camera size={14} className="mr-1" />} Gunakan Frame Ini
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
